import React, { useEffect, useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { toast } from '@cotera/client/app/components/ui';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import { cn, FOCUS_RING } from '@cotera/client/v0/lib/utils';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { AgentEmojiPicker } from './agent-emoji-picker';
import { useAgentModel } from './agent.model';
import { SaveAgentAction } from './save-agent.action';
import {
  clearPendingEmoji,
  setPendingEmoji,
  usePendingEmoji,
} from './agent-emoji-pending-store';

export const AgentEmojiHeaderButton: React.FC<{
  agentId: string;
  emoji: string | null;
  editable?: boolean;
}> = ({ agentId, emoji, editable = true }) => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const model = useAgentModel();
  const saveAgentAction = useAction(SaveAgentAction);
  const resource = useWatchableValue(model.resourceWatchable);
  const permission = resource?.meta?.permission;
  // meta is null for unsaved agents — owned by the user, treat as editable.
  const canEdit =
    resource === null ||
    resource.meta === null ||
    permission === 'edit' ||
    permission === 'admin';
  const effectiveEditable = editable && canEdit;
  const [open, setOpen] = useState(false);
  const pendingEmoji = usePendingEmoji(agentId);
  const pendingRequestRef = React.useRef(0);

  const save = async (next: string | null) => {
    setOpen(false);
    const previous = emoji;
    const requestId = ++pendingRequestRef.current;
    setPendingEmoji(agentId, next);
    void model.setAgentEmoji(next);
    if (model.isNew) {
      // Persist the agent first (autosave may already have created it — create
      // then becomes update). Fold emoji into create when possible.
      const saveResult = await saveAgentAction.execute({
        silent: true,
        agentId,
        emoji: next,
      });
      if (saveResult.isErr()) {
        if (pendingRequestRef.current === requestId) {
          clearPendingEmoji(agentId);
        }
        void model.setAgentEmoji(previous);
        toast.error('Failed to save agent before setting emoji');
        return;
      }
      // Always setEmoji after save: isNew stays true after autosave, so create
      // may hit the update path and historically dropped emoji (COT-6421).
    }
    const result = await client.resource.setEmoji({
      type: ResourceType.Agent,
      id: agentId,
      emoji: next,
    });
    if (result.isErr()) {
      if (pendingRequestRef.current === requestId) {
        clearPendingEmoji(agentId);
      }
      void model.setAgentEmoji(previous);
      const appErr = result.error.t === 'application' ? result.error.err : null;
      toast.error(
        appErr?.errorType === 'Forbidden'
          ? "You don't have permission to edit this agent"
          : 'Failed to update emoji'
      );
      return;
    }
    const key = queryKeys.resource.get({
      orgId,
      type: ResourceType.Agent,
      id: agentId,
    });
    await queryClient.invalidateQueries({ queryKey: key });
  };

  const displayedEmoji = pendingEmoji !== undefined ? pendingEmoji : emoji;
  const hasEmoji = displayedEmoji !== null && displayedEmoji !== '';

  // Clear pending once the broadcast catches up — clearing on HTTP response
  // would race the broadcast and briefly flash the prior emoji.
  useEffect(() => {
    if (pendingEmoji !== undefined && pendingEmoji === emoji) {
      clearPendingEmoji(agentId);
    }
  }, [pendingEmoji, emoji, agentId]);

  return (
    <Popover
      open={effectiveEditable ? open : false}
      onOpenChange={effectiveEditable ? setOpen : undefined}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!effectiveEditable}
          aria-label={hasEmoji ? 'Change agent emoji' : 'Add agent emoji'}
          className={cn(
            'inline-flex items-center justify-start rounded-md transition-all duration-100 active:scale-99',
            FOCUS_RING,
            hasEmoji
              ? '-ml-[14px] p-[4px] leading-none text-5xl hover:bg-sidebar-accent'
              : // The sidebar's command rows (Search, New chat, Library) are the
                // app's house style for "an icon and a label you can click":
                // `gap-2 rounded-md p-2 text-sm`, a `size-4` icon, and the
                // sidebar-accent hover. This used to be a bare `'+ Add icon'`
                // string at `text-xs` — a glyph typed into a label rather than an
                // icon, at a size nothing else in the app uses.
                //
                // No horizontal padding and no compensating negative margin:
                // the icon's left edge *is* the row's inset, with nothing to
                // cancel out. The pair it used to carry (`p-2` undone by
                // `-ml-2`) was a net zero that only held while both agreed, and
                // it made every alignment question a question about two classes
                // instead of one number. Vertical breathing room for the hover
                // target comes from `py-1.5 -my-1.5`, which costs no width.
                // `-mx-2 px-2` and `-my-1.5 py-1.5` cancel on both axes, so the
                // hover target has room to breathe while the icon's left edge
                // still lands exactly on the row's inset.
                'gap-2 -mx-2 -my-1.5 px-2 py-1.5 text-sm text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0',
            effectiveEditable ? 'cursor-pointer' : 'cursor-default'
          )}
        >
          {hasEmoji ? (
            displayedEmoji
          ) : effectiveEditable ? (
            <>
              <SmilePlus aria-hidden="true" />
              <span>Add icon</span>
            </>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-fit max-w-fit p-0"
      >
        <AgentEmojiPicker
          emoji={displayedEmoji}
          onSelect={(next) => void save(next)}
          onRemove={() => void save(null)}
        />
      </PopoverContent>
    </Popover>
  );
};
